import { useMemo, useState } from 'react';
import { pkPairs, type PkPair } from '@/mocks/ranking';
import { fmtCount, fmtPercent } from '@/lib/format';
import SectionHeading from './SectionHeading';

const catOf = (p: PkPair) =>
  p.theme.includes('综艺') ? '综艺' : p.theme.includes('影视') ? '影视' : p.theme.includes('网红') ? '网红' : '萌宠';
const tabs = ['全部', '综艺', '影视', '网红', '萌宠'];

export default function PkSection() {
  const [tab, setTab] = useState('全部');
  const [voted, setVoted] = useState<Record<number, 'a' | 'b' | null>>({});

  const list = useMemo(() => (tab === '全部' ? pkPairs : pkPairs.filter((p) => catOf(p) === tab)), [tab]);

  const pick = (id: number, side: 'a' | 'b') =>
    setVoted((v) => ({ ...v, [id]: v[id] === side ? null : side }));

  const resetAll = () => setVoted({});

  return (
    <section id="pk" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="网友票选 · 支持率实时刷新"
          title="人气PK"
          desc="同一场对决，两种不同立场。点选你站的那一边，看看全网的你能否用一票逆转局势。"
        />

        {/* Category pills */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2 rounded-full bg-background-50 p-1">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  tab === t
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-200/60'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-200/50"
          >
            <i className="ri-refresh-line text-sm leading-none" />
            重置我的票
          </button>
        </div>

        {/* PK cards */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {list.map((pair) => {
            const mySide = voted[pair.id];
            const aVotes = pair.a.votes + (mySide === 'a' ? 1 : 0);
            const bVotes = pair.b.votes + (mySide === 'b' ? 1 : 0);
            const ratioA = fmtPercent(aVotes, bVotes);
            const lead = ratioA >= 50 ? 'A' : 'B';
            return (
              <article key={pair.id} className="overflow-hidden rounded-3xl border border-background-200 bg-background-50">
                <div className="flex items-center justify-between gap-2 border-b border-background-200 bg-background-100/60 px-5 py-3">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                    <i className="ri-vip-crown-fill text-base leading-none text-accent-500" />
                    {pair.theme}
                  </h3>
                  <span className="whitespace-nowrap rounded-full bg-background-50 px-2.5 py-1 text-[11px] text-foreground-500">
                    {fmtCount(aVotes + bVotes)}票已参与
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
                  {/* Option A */}
                  <button
                    onClick={() => pick(pair.id, 'a')}
                    className={`group flex flex-col items-center gap-1 rounded-2xl border-2 p-4 text-center transition-all ${
                      mySide === 'a'
                        ? 'border-primary-500 bg-primary-100/70'
                        : 'border-background-200 bg-background-100/50 hover:border-primary-300 hover:bg-primary-50'
                    }`}
                  >
                    <span
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 text-background-50 ${
                        mySide === 'a' ? 'animate-flame' : ''
                      }`}
                    >
                      <i className="ri-flashlight-fill text-2xl leading-none" />
                    </span>
                    <span className="mt-1 text-base font-bold text-foreground-950">{pair.a.name}</span>
                    <span className="text-xs text-foreground-500">{pair.a.desc}</span>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
                      {mySide === 'a' && <i className="ri-checkbox-circle-fill leading-none" />}
                      {fmtCount(aVotes)}票 · {ratioA}%
                    </span>
                  </button>

                  {/* Option B */}
                  <button
                    onClick={() => pick(pair.id, 'b')}
                    className={`group flex flex-col items-center gap-1 rounded-2xl border-2 p-4 text-center transition-all ${
                      mySide === 'b'
                        ? 'border-secondary-500 bg-secondary-100/70'
                        : 'border-background-200 bg-background-100/50 hover:border-secondary-300 hover:bg-secondary-50'
                    }`}
                  >
                    <span
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-500 text-background-50 ${
                        mySide === 'b' ? 'animate-flame' : ''
                      }`}
                    >
                      <i className="ri-heart-fill text-2xl leading-none" />
                    </span>
                    <span className="mt-1 text-base font-bold text-foreground-950">{pair.b.name}</span>
                    <span className="text-xs text-foreground-500">{pair.b.desc}</span>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-secondary-600">
                      {mySide === 'b' && <i className="ri-checkbox-circle-fill leading-none" />}
                      {fmtCount(bVotes)}票 · {100 - ratioA}%
                    </span>
                  </button>
                </div>

                {/* progress + lead */}
                <div className="px-5 pb-5">
                  <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-background-200">
                    <div className="bg-primary-500 transition-all duration-500" style={{ width: `${ratioA}%` }} />
                    <div className="flex-1 bg-secondary-500 transition-all duration-500" />
                  </div>
                  <p className="mt-2 text-xs text-foreground-500">
                    {mySide ? (
                      <>
                        已投票：{mySide === 'a' ? pair.a.name : pair.b.name} · 再点一次可撤销
                      </>
                    ) : (
                      <>目前 {lead === 'A' ? pair.a.name : pair.b.name} 领先，投出你的一票吧</>
                    )}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}